import type { CartBundleGroup, CartItem } from '#types/cart'

/**
 * Splits a bundle promotion's callout message into its PDP and cart/mini-cart variants.
 *
 * Both the Product (PLA) `calloutMessage` and Cart `calloutMsg` fields pack two messages into a
 * single pipe-delimited string - the PDP-facing message first, the cart/mini-cart-facing message
 * second (e.g. `"Bundle up and save 20% | Congrats, 20% unlocked!"`). When there is no pipe, the
 * same message is used for both.
 * @param message - the raw callout message from the Product or Cart API (optional)
 * @returns `pdp` and `cart` message variants, trimmed
 * @category Utils
 */
export const splitBundleCalloutMessage = (message?: string): { pdp: string, cart: string } => {
  const [pdp = '', cart = pdp] = (message ?? '').split('|').map((part) => part.trim())

  return { pdp, cart }
}

/**
 * Checks whether a real cart item carries a `productPromotions` entry for the given bundle
 * promotion id - items qualify for the same bundle when they share a `promotionId`.
 * @param item - a real cart item
 * @param promotionId - the bundle promotion id to check against
 * @returns True if the item qualifies for the bundle promotion
 * @category Utils
 */
export const isBundleQualifiedCartItem = (item: CartItem, promotionId: string): boolean =>
  !!item.productPromotions?.some((promotion) => promotion.promotionId === promotionId)

/**
 * Checks whether a cart upsell recommendation's product is already present in the cart, so it
 * can be filtered out of the upsell rail (cart and mini-cart both show "complete the look"
 * upsells next to the real cart items, and a product already in the cart shouldn't be
 * recommended again). A mapped `ProductUpsell.id` can carry a trailing variant segment
 * (`styleId:color:size`) that the matching cart item's `masterId` (`styleId:color`) doesn't, so
 * matching on the shared `styleId:color` prefix catches these instead of requiring exact equality.
 * @param upsellId - the mapped `ProductUpsell.id` to check
 * @param items - the real cart items to check against
 * @returns True if a cart item's `masterId` starts with the upsell id's `styleId:color` prefix
 * @category Utils
 */
export const isUpsellInCart = (upsellId: string, items: CartItem[]): boolean => {
  const masterIdPrefix = upsellId.split(':').slice(0, 2).join('')

  return items.some(({ masterId }) => masterId.startsWith(masterIdPrefix))
}

/**
 * Groups cart items that share a `productPromotions[].promotionId`. Promotion ids are collected
 * directly from the items themselves (a promotion only "counts" as a bundle once >= 2 cart items
 * carry it); `calloutMessage` is the cart/mini-cart half of a qualifying item's own
 * `productPromotions[].calloutMsg` (which packs the PDP message and the cart/mini-cart message
 * together, pipe-delimited - see `splitBundleCalloutMessage`).
 * @param items - the cart items to group (either `cart.items`, or a pre-filtered subset)
 * @returns The bundle groups (promotions with >= 2 qualifying items) and any standalone items left over
 * @category Utils
 */
export const groupCartItemsByBundlePromotion = (items: CartItem[]) => {
  const usedIds = new Set<string>()

  const promotionIds = [...new Set(
    items.flatMap((item) => item.productPromotions
      ?.map(({ promotionId }) => promotionId)
      .filter((id): id is string => !!id && id.toUpperCase().includes('BUNDLE')) ?? [])
  )]

  const bundleGroups: CartBundleGroup[] = promotionIds
    .map((promotionId) => {
      const qualifyingItems = items.filter(
        (item) => !usedIds.has(item.id) && isBundleQualifiedCartItem(item, promotionId)
      )
      const productPromotion = qualifyingItems[0]?.productPromotions
        ?.find((promo) => promo.promotionId === promotionId)

      return {
        promotion: productPromotion && {
          calloutMessage: splitBundleCalloutMessage(productPromotion.calloutMsg).cart,
          promotionId: productPromotion.promotionId
        },
        items: qualifyingItems
      }
    })
    .filter((group): group is CartBundleGroup => !!group.promotion && group.items.length >= 2)

  bundleGroups.forEach(({ items }) => items.forEach(({ id }) => usedIds.add(id)))

  return {
    bundleGroups,
    standaloneItems: items.filter((item) => !usedIds.has(item.id))
  }
}
